import { useEffect, useMemo, useRef, useState } from 'react';
import { useApp, notificationPath, STATUS_MESSAGE_MAX } from '../state/AppState.jsx';
import { navigate } from '../lib/router.js';
import { supabase, unwrap } from '../lib/supabase.js';
import { relativeTime } from '../lib/dates.js';
import * as Icon from '../lib/icons.jsx';
import {
  Avatar, Emblem, Popover, useDisclosure, MenuItem, MenuSeparator, MenuLabel,
  Modal, Field, EmptyState, ColorSwatchPicker, BOARD_COLORS, useIsPhone,
} from './ui.jsx';

const NAV = [
  { path: '/', label: 'Dashboard', icon: Icon.Home },
  { path: '/my-work', label: 'My work', icon: Icon.Inbox },
  { path: '/boards', label: 'All boards', icon: Icon.Grid },
  { path: '/chats', label: 'Chats', icon: Icon.Message },
  { path: '/notes', label: 'Notes', icon: Icon.FileText },
  { path: '/reports', label: 'Reports', icon: Icon.Chart },
  { path: '/people', label: 'People', icon: Icon.Users },
];

/**
 * Whether the sidebar is a rail. Remembered, because it is a statement about
 * the screen you work on rather than about this visit, and re-collapsing it
 * every morning would make the setting not worth having.
 */
const RAIL_KEY = 'pulse.sidebar.collapsed';

export default function Shell({ route, children }) {
  /* A file dropped anywhere that does not take files would make the browser
     open it in place of Pulse, losing whatever was being typed. Places that
     take files (the message composer, a note) handle the drop themselves
     and come first; everywhere else the drop is simply refused. Only drags
     that carry files are touched, so moving rows and cards is unaffected. */
  useEffect(() => {
    const filesIn = (event) => Array.from(event.dataTransfer?.types || []).includes('Files');
    const over = (event) => {
      if (!filesIn(event) || event.defaultPrevented) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'none';
    };
    const drop = (event) => { if (filesIn(event)) event.preventDefault(); };
    window.addEventListener('dragover', over);
    window.addEventListener('drop', drop);
    return () => {
      window.removeEventListener('dragover', over);
      window.removeEventListener('drop', drop);
    };
  }, []);

  const app = useApp();
  const [newBoardOpen, setNewBoardOpen] = useState(false);
  const [newWorkspaceOpen, setNewWorkspaceOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(RAIL_KEY) === '1');
  const boardMatch = route.match('/boards/:id');

  /* Collapsing happens with the pointer ON the sidebar - the button is in it -
     and a collapsed sidebar expands on hover. So the click appeared to do
     nothing: the grid track shrank to 64px, the content slid left, and the nav
     stayed 248px wide because it was still being hovered. It only became a rail
     once the pointer went somewhere else.

     Peeking is therefore suppressed until the pointer genuinely leaves.

     FOCUS, NOT JUST HOVER. Gating only the hover rules fixed nothing, because
     clicking the button also leaves focus inside the sidebar and :focus-within
     held it open by itself - which is what the measurement showed: the track
     was 64px and the nav still 248px. Both are suppressed, and both release:
     the pointer leaving, or focus leaving, is enough. */
  const [peekOff, setPeekOff] = useState(false);

  const toggleRail = () => setCollapsed((current) => {
    const next = !current;
    localStorage.setItem(RAIL_KEY, next ? '1' : '0');
    if (next) setPeekOff(true);
    return next;
  });

  const crumbBoard = boardMatch ? app.boards.find((b) => b.id === boardMatch.id) : null;

  const title = useMemo(() => {
    if (boardMatch) return app.boards.find((b) => b.id === boardMatch.id)?.name || 'Board';
    // /chats/:id is still Chats. Without this it falls past the exact match and
    // an open conversation is titled "Dashboard".
    if (route.path.startsWith('/chats')) return 'Chats';
    if (route.path.startsWith('/notes')) return 'Notes';
    return NAV.find((n) => n.path === route.path)?.label
      || (route.path === '/settings' ? 'Settings' : 'Dashboard');
  }, [route.path, boardMatch, app.boards]);

  return (
    <div className={`shell${collapsed ? ' collapsed' : ''}${peekOff ? ' no-peek' : ''}`}>
      <Sidebar
        route={route}
        onNewBoard={() => setNewBoardOpen(true)}
        onNewWorkspace={() => setNewWorkspaceOpen(true)}
        collapsed={collapsed}
        onToggleRail={toggleRail}
        onReleasePeek={() => setPeekOff(false)}
      />
      <div className="main">
        <div className="topbar">
          {/* On a board this is a breadcrumb carrying the board's own mark, and
              it is the ONLY place the board is named. The page used to print
              the name here and again in a title row twelve pixels below it,
              which cost a whole row of vertical space to say the same thing
              twice. */}
          {boardMatch ? (
            <div className="topbar-title crumbs">
              {/* Under 860px the sidebar is gone and the workspace crumb goes
                  with it, which left a board with no way back at all - the
                  native shells have no browser chrome to fall back on. */}
              <button
                type="button"
                className="icon-btn crumb-back"
                onClick={() => navigate('/boards')}
                aria-label="Back to boards"
              >
                <Icon.ChevronLeft size={18} />
              </button>
              <button
                type="button"
                className="crumb-ws"
                onClick={() => navigate('/boards')}
                title="All boards"
              >
                {app.workspace?.name || 'Workspace'}
              </button>
              <Icon.ChevronRight size={14} className="crumb-sep" />
              <Emblem
                name={crumbBoard?.name}
                icon={crumbBoard?.icon}
                color={crumbBoard?.color}
                size="xs"
              />
              <span className="crumb-board">{title}</span>
            </div>
          ) : (
            <div className="topbar-title">{title}</div>
          )}
          <div className="topbar-spacer" />
          {/* On a phone a board's crumb needs its row, so search collapses to
              a button there and expands over the bar when tapped. */}
          <GlobalSearch compact={!!boardMatch} />
          <NotificationBell />
          <button
            type="button"
            className="icon-btn"
            onClick={app.toggleTheme}
            aria-label={app.theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            title={app.theme === 'dark' ? 'Light theme' : 'Dark theme'}
          >
            {app.theme === 'dark' ? <Icon.Sun size={17} /> : <Icon.Moon size={17} />}
          </button>
          <UserMenu onNewWorkspace={() => setNewWorkspaceOpen(true)} />
        </div>
        <div className="content">{children}</div>
        <BottomNav route={route} unread={app.unread} />
      </div>
      <NewBoardModal open={newBoardOpen} onClose={() => setNewBoardOpen(false)} />
      <NewWorkspaceModal open={newWorkspaceOpen} onClose={() => setNewWorkspaceOpen(false)} />
    </div>
  );
}

/**
 * Navigation on a phone.
 *
 * The sidebar is hidden below 860px and, until now, nothing replaced it. That
 * left a phone able to reach the Dashboard it lands on and nothing else: no
 * board, no My work, no Reports. The board views were being polished behind a
 * door with no handle.
 *
 * Shown and hidden purely in CSS, at the same 860px the sidebar disappears at,
 * so the two can never disagree and leave a width with no navigation at all -
 * which is exactly what a JS breakpoint of its own would risk.
 *
 * People is left out: five targets is too many across a phone, and it is
 * already in the account menu. Reports left with it when Chats arrived, on the
 * same rule - a chart you cannot read on a phone is a worse use of a quarter of
 * the bar than the conversation you actually reach for on one. Both are still a
 * tap away in the sidebar on anything wider.
 */
function BottomNav({ route, unread }) {
  const items = NAV.filter((item) => item.path !== '/people' && item.path !== '/reports');

  return (
    <nav className="bottom-nav" aria-label="Main">
      {items.map((item) => {
        // Prefix, not equality: /chats/:id and /notes/:id belong to their tab.
        const active = item.path === '/' ? route.path === '/' : route.path.startsWith(item.path);
        return (
          <button
            key={item.path}
            type="button"
            className={`bottom-nav-item${active ? ' active' : ''}`}
            aria-current={active ? 'page' : undefined}
            onClick={() => navigate(item.path)}
          >
            <span className="bottom-nav-icon">
              <item.icon size={19} />
              {item.path === '/my-work' && unread > 0 && (
                <span className="bottom-nav-badge">{unread > 9 ? '9+' : unread}</span>
              )}
            </span>
            <span className="bottom-nav-label">{item.label}</span>
          </button>
        );
      })}
    </nav>
  );
}

/* ------------------------------------------------------------------ sidebar */

function Sidebar({ route, onNewBoard, onNewWorkspace, collapsed, onToggleRail, onReleasePeek }) {
  const app = useApp();
  const workspaceMenu = useDisclosure();
  const boardMatch = route.match('/boards/:id');

  return (
    /* Either gesture releases the suppression: taking the pointer away, or
       moving focus on. onBlur is React's focusout, so it fires when focus
       moves between children too - which is right, because a keyboard user
       tabbing through the sidebar is someone who wants to read it. */
    <aside className="sidebar" onPointerLeave={onReleasePeek} onBlur={onReleasePeek}>
      <div className="brand">
        <span className="brand-mark">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M3 13h4l3-8 4 15 3-9h4" />
          </svg>
        </span>
        <span className="brand-name rail-hide">Pulse</span>
        {/* Sits in the brand row rather than the footer so it is in the same
            place whether or not there are boards to scroll past. */}
        <button
          type="button"
          className="icon-btn sm rail-toggle"
          onClick={onToggleRail}
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Expand the sidebar' : 'Collapse the sidebar'}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <Icon.ChevronRight size={15} /> : <Icon.ChevronLeft size={15} />}
        </button>
      </div>

      <div className="ws-picker">
        <button type="button" className="ws-button" ref={workspaceMenu.anchor} onClick={workspaceMenu.toggle}>
          <Emblem
            name={app.workspace?.name || '?'}
            icon={app.workspace?.icon}
            color="var(--brand)"
            size="sm"
          />
          <span className="ws-meta rail-hide">
            <span className="ws-name">{app.workspace?.name || 'No workspace'}</span>
            <span className="ws-sub">
              {app.workspace ? `${app.workspace.memberCount} member${app.workspace.memberCount === 1 ? '' : 's'}` : ''}
            </span>
          </span>
          <Icon.ChevronDown size={15} style={{ color: 'var(--muted)' }} className="rail-hide" />
        </button>
        <Popover anchor={workspaceMenu.anchor} open={workspaceMenu.open} onClose={workspaceMenu.close} width={228}>
          <MenuLabel>Workspaces</MenuLabel>
          {app.workspaces.map((workspace) => (
            <MenuItem
              key={workspace.id}
              selected={workspace.id === app.workspaceId}
              onClick={() => { app.switchWorkspace(workspace.id); workspaceMenu.close(); }}
            >
              {workspace.name}
            </MenuItem>
          ))}
          <MenuSeparator />
          <MenuItem icon={Icon.Plus} onClick={() => { workspaceMenu.close(); onNewWorkspace(); }}>
            New workspace
          </MenuItem>
        </Popover>
      </div>

      <nav className="nav">
        {NAV.map((item) => (
          <button
            key={item.path}
            type="button"
            /* Prefix match, not equality, so an open conversation still lights
               up Chats. Exact match would leave the sidebar showing nothing
               selected on every /chats/:id. */
            className={`nav-item${route.path === item.path || (item.path !== '/' && route.path.startsWith(`${item.path}/`)) ? ' active' : ''}`}
            onClick={() => navigate(item.path)}
            /* The label is the tooltip when it is the only thing hidden. */
            title={item.label}
          >
            <item.icon size={16} />
            <span className="rail-hide">{item.label}</span>
            {item.path === '/my-work' && app.unread > 0 && <span className="count">{app.unread}</span>}
          </button>
        ))}
      </nav>

      <div className="side-section">
        <span className="rail-hide">Boards</span>
        <button type="button" className="icon-btn sm" onClick={onNewBoard} aria-label="New board" title="New board">
          <Icon.Plus size={15} />
        </button>
      </div>

      <div className="board-list">
        {app.boards.map((board) => (
          <SidebarBoardLink
            key={board.id}
            board={board}
            active={boardMatch?.id === board.id}
          />
        ))}
        {!app.boards.length && (
          /* rail-hide, or this paragraph reflows into a 64px column and the
             rail becomes a tall stack of one-word lines. */
          <p className="rail-hide" style={{ padding: '4px 10px', fontSize: 12.5, color: 'var(--muted)' }}>
            No boards yet. Create your first one.
          </p>
        )}
      </div>

      <div className="sidebar-foot">
        <button type="button" className="btn block" onClick={onNewBoard} title="New board">
          <Icon.Plus size={15} />
          <span className="rail-hide">New board</span>
        </button>
      </div>
    </aside>
  );
}

/* ------------------------------------------------------------ global search */

function GlobalSearch({ compact = false }) {
  const app = useApp();
  const isPhone = useIsPhone();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ items: [], boards: [], notes: [] });
  const [open, setOpen] = useState(false);
  /* On a phone board page the box starts as an icon: the crumb - the only way
     back - keeps its row, and tapping the icon expands the search OVER the
     bar, the way every phone app resolves "title or search". */
  const [expanded, setExpanded] = useState(false);
  const overlay = compact && isPhone;
  const boxRef = useRef(null);
  const inputRef = useRef(null);

  const collapse = () => {
    setExpanded(false);
    setOpen(false);
    setQuery('');
  };

  // Leaving the board (or growing past phone width) resets the overlay.
  useEffect(() => { if (!overlay) setExpanded(false); }, [overlay]);
  useEffect(() => { if (expanded) inputRef.current?.focus(); }, [expanded]);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults({ items: [], boards: [], notes: [] });
      return undefined;
    }
    // Debounced so a fast typist issues one request, not one per keystroke.
    const timer = setTimeout(async () => {
      /* EVERY workspace, not just the open one - the RPC is per-workspace and
         RLS-checked, so one call per membership and merge. The open workspace
         leads; hits from the others carry the workspace's name so it is clear
         where the click will land. */
      // Notes are the person's, not a workspace's: one call, RLS-scoped.
      const notesCall = app.guard(
        async () => unwrap(await supabase.rpc('search_notes', { p_query: query, p_limit: 8 })),
        { silent: true },
      );
      const perWorkspace = await Promise.all(app.workspaces.map(async (ws) => ({
        ws,
        data: await app.guard(
          async () => unwrap(await supabase.rpc('search_board_items', {
            p_workspace_id: ws.id,
            p_query: query,
          })),
          { silent: true },
        ),
      })));
      const merged = { items: [], boards: [] };
      for (const { ws, data } of perWorkspace
        .sort((a, b) => (b.ws.id === app.workspaceId) - (a.ws.id === app.workspaceId))) {
        if (!data) continue;
        const tag = { workspaceId: ws.id, workspaceName: ws.name };
        for (const board of data.boards || []) merged.boards.push({ ...board, ...tag });
        for (const item of data.items || []) merged.items.push({ ...item, ...tag });
      }
      const notes = (await notesCall) || [];
      setResults({ boards: merged.boards.slice(0, 8), items: merged.items.slice(0, 20), notes });
    }, 200);
    return () => clearTimeout(timer);
  }, [query, app.workspaceId, app.workspaces]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    const onClick = (event) => { if (!boxRef.current?.contains(event.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  /* A hit in another workspace switches there first - switchWorkspace takes
     the destination, so it lands on the result rather than on that
     workspace's dashboard. */
  const go = (path, workspaceId) => {
    setExpanded(false);
    setOpen(false);
    setQuery('');
    if (workspaceId && workspaceId !== app.workspaceId) app.switchWorkspace(workspaceId, path);
    else navigate(path);
  };

  const hasResults = results.boards.length > 0 || results.items.length > 0 || results.notes.length > 0;

  if (overlay && !expanded) {
    return (
      <button
        type="button"
        className="icon-btn"
        onClick={() => setExpanded(true)}
        aria-label="Search"
        title="Search"
      >
        <Icon.Search size={17} />
      </button>
    );
  }

  return (
    <div className={`search${overlay ? ' search-overlay' : ''}`} ref={boxRef}>
      <Icon.Search size={15} className="search-icon" />
      <input
        ref={inputRef}
        className="search-input"
        placeholder={isPhone ? 'Search' : 'Search items and boards    ⌘K'}
        value={query}
        onChange={(event) => { setQuery(event.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            if (overlay) collapse();
            else setOpen(false);
            event.currentTarget.blur();
          }
        }}
      />
      {overlay && (
        <button
          type="button"
          className="icon-btn sm search-cancel"
          onClick={collapse}
          aria-label="Close search"
        >
          <Icon.X size={15} />
        </button>
      )}
      {open && query.trim().length >= 2 && (
        <div className="search-panel">
          {!hasResults && (
            <p style={{ padding: '14px 12px', fontSize: 13, color: 'var(--muted)' }}>
              Nothing matches “{query.trim()}”.
            </p>
          )}
          {results.boards.length > 0 && (
            <>
              <div className="search-group">Boards</div>
              {results.boards.map((board) => (
                <button
                  key={board.id}
                  type="button"
                  className="search-hit"
                  onClick={() => go(`/boards/${board.id}`, board.workspaceId)}
                >
                  <span className="board-dot" style={{ background: board.color }} />
                  <span className="search-hit-main">
                    <span className="search-hit-name">{board.name}</span>
                    {board.workspaceId !== app.workspaceId && (
                      <span className="search-hit-meta">{board.workspaceName}</span>
                    )}
                  </span>
                </button>
              ))}
            </>
          )}
          {results.items.length > 0 && (
            <>
              <div className="search-group">Items</div>
              {results.items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="search-hit"
                  onClick={() => go(`/boards/${item.boardId}?item=${item.id}`, item.workspaceId)}
                >
                  <span className="board-dot" style={{ background: item.boardColor }} />
                  <span className="search-hit-main">
                    <span className="search-hit-name">{item.name}</span>
                    <span className="search-hit-meta">
                      {item.boardName} · {item.groupTitle}
                      {item.workspaceId !== app.workspaceId && ` · ${item.workspaceName}`}
                      {item.matchedIn === 'content' && ' · matched in content'}
                    </span>
                  </span>
                </button>
              ))}
            </>
          )}
          {results.notes.length > 0 && (
            <>
              <div className="search-group">Notes</div>
              {results.notes.map((note) => (
                <button
                  key={note.id}
                  type="button"
                  className="search-hit"
                  onClick={() => go(`/notes/${note.id}`)}
                >
                  <span className="search-hit-icon"><Icon.FileText size={14} /></span>
                  <span className="search-hit-main">
                    <span className="search-hit-name">{note.title?.trim() || 'Untitled'}</span>
                    {note.excerpt && (
                      <span className="search-hit-meta">
                        {note.matchedIn === 'content' ? note.excerpt : `Edited ${relativeTime(note.updatedAt)}`}
                      </span>
                    )}
                  </span>
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------- bell + user */

const NOTIFICATION_STYLE = {
  assigned: { icon: Icon.Person, tone: 'var(--info)', soft: 'var(--info-soft)' },
  mention: { icon: Icon.Users, tone: 'var(--brand)', soft: 'var(--brand-soft)' },
  // @all. Same family as a mention, warmer, because it is louder.
  mention_all: { icon: Icon.Users, tone: 'var(--warn)', soft: 'var(--warn-soft)' },
  comment: { icon: Icon.Text, tone: 'var(--brand)', soft: 'var(--brand-soft)' },
  reply: { icon: Icon.Reply, tone: 'var(--brand)', soft: 'var(--brand-soft)' },
  completed: { icon: Icon.Check, tone: 'var(--good)', soft: 'var(--good-soft)' },
  due_soon: { icon: Icon.Clock, tone: 'var(--warn)', soft: 'var(--warn-soft)' },
  overdue: { icon: Icon.Alert, tone: 'var(--danger)', soft: 'var(--danger-soft)' },
  invite: { icon: Icon.Users, tone: 'var(--brand)', soft: 'var(--brand-soft)' },
  workspace_added: { icon: Icon.Users, tone: 'var(--good)', soft: 'var(--good-soft)' },
  group_added: { icon: Icon.Message, tone: 'var(--good)', soft: 'var(--good-soft)' },
  // A direct message is the one alert that is not about a board at all.
  dm: { icon: Icon.Message, tone: 'var(--good)', soft: 'var(--good-soft)' },
  // Notes: shared with you, named in one, a comment on one, a time you set.
  note_shared: { icon: Icon.FileText, tone: 'var(--brand)', soft: 'var(--brand-soft)' },
  note_mention: { icon: Icon.FileText, tone: 'var(--brand)', soft: 'var(--brand-soft)' },
  note_comment: { icon: Icon.Message, tone: 'var(--brand)', soft: 'var(--brand-soft)' },
  note_reminder: { icon: Icon.Clock, tone: 'var(--warn)', soft: 'var(--warn-soft)' },
  update: { icon: Icon.Bell, tone: 'var(--text-2)', soft: 'var(--surface-3)' },
};

/** Past this many pixels the drag counts as a swipe rather than a click. */
const SWIPE_COMMIT = 72;
const SWIPE_SLOP = 8;

/**
 * One alert, draggable.
 *
 * Right marks it read or unread, left throws it away - the phone conventions,
 * and they work with a mouse too because pointer events do not care which one
 * you have. A drag shorter than the slop is a click and opens the alert, so
 * the gesture costs the ordinary case nothing.
 */
function SwipeRow({ notification, style, onOpen, onToggleRead, onDelete }) {
  const [dx, setDx] = useState(0);
  const start = useRef(null);
  const swiped = useRef(false);

  const end = () => {
    if (start.current === null) return;
    const travelled = dx;
    start.current = null;
    setDx(0);
    if (travelled <= -SWIPE_COMMIT) onDelete();
    else if (travelled >= SWIPE_COMMIT) onToggleRead();
  };

  return (
    <div className={`notif-swipe${dx < 0 ? ' left' : ''}${dx > 0 ? ' right' : ''}`}>
      {/* Behind the row, revealed as it moves off whichever edge. */}
      <span className="notif-behind read" aria-hidden="true">
        <Icon.Check size={15} />
        {notification.read ? 'Unread' : 'Read'}
      </span>
      <span className="notif-behind delete" aria-hidden="true">
        <Icon.Trash size={15} />
        Delete
      </span>

      <div
        className={`notif${notification.read ? '' : ' unread'}`}
        style={dx ? { transform: `translateX(${dx}px)`, transition: 'none' } : undefined}
        onPointerDown={(event) => {
          // Ignore the buttons on the right, and anything with its own drag.
          if (event.button !== 0) return;
          start.current = event.clientX;
          swiped.current = false;
        }}
        onPointerMove={(event) => {
          if (start.current === null) return;
          const moved = event.clientX - start.current;
          if (Math.abs(moved) < SWIPE_SLOP) return;
          swiped.current = true;
          event.currentTarget.setPointerCapture?.(event.pointerId);
          setDx(moved);
        }}
        onPointerUp={end}
        onPointerCancel={end}
        onPointerLeave={end}
      >
        <button
          type="button"
          className="notif-main"
          // A swipe releases over the row and would otherwise also fire this.
          onClick={() => { if (!swiped.current) onOpen(); }}
        >
          <span className="notif-kind" style={{ background: style.soft, color: style.tone }}>
            <style.icon size={15} />
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span className="notif-title" style={{ display: 'block' }}>{notification.title}</span>
            {notification.body && <span className="notif-body" style={{ display: 'block' }}>{notification.body}</span>}
            <span className="notif-time" style={{ display: 'block' }}>{relativeTime(notification.createdAt)}</span>
          </span>
        </button>

        {/* Buttons as well as gestures: a swipe is undiscoverable on its own,
            and a keyboard cannot perform one at all. */}
        <button
          type="button"
          className="notif-act"
          title={notification.read ? 'Mark as unread' : 'Mark as read'}
          aria-label={`Mark "${notification.title}" as ${notification.read ? 'unread' : 'read'}`}
          onClick={onToggleRead}
        >
          {notification.read ? <Icon.Eye size={14} /> : <Icon.Check size={14} />}
        </button>
        <button
          type="button"
          className="notif-act danger"
          title="Delete"
          aria-label={`Delete "${notification.title}"`}
          onClick={onDelete}
        >
          <Icon.Trash size={13} />
        </button>
      </div>
    </div>
  );
}

/**
 * One board in the rail - and, when it carries a badge, a hover preview of
 * exactly what is shouting. The badge said "3" and made you go find out; the
 * preview says WHAT, and one click lands on the comment itself.
 *
 * Same hover-intent delay as the table's comment preview: crossing the rail
 * on the way somewhere else must not detonate popovers.
 */
function SidebarBoardLink({ board, active }) {
  const app = useApp();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  // Unread notifications from this board: new comments, replies, mentions and
  // assignments. The bell holds the most recent 40, so a huge backlog previews
  // its newest slice - which is the useful slice.
  const unread = app.unreadByBoard[board.id] || 0;
  const preview = app.notifications
    .filter((n) => n.boardId === board.id && !n.read)
    .slice(0, 6);

  useEffect(() => () => clearTimeout(timer.current), []);
  const enter = () => {
    if (!unread || !preview.length) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(true), 300);
  };
  const leave = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(false), 220);
  };

  const openNotification = (notification) => {
    app.markNotificationRead(notification.id);
    setOpen(false);
    const target = notificationPath(notification);
    if (target) navigate(target);
  };

  return (
    <>
      <button
        type="button"
        ref={anchorRef}
        className={`board-link${active ? ' active' : ''}`}
        onClick={() => { setOpen(false); navigate(`/boards/${board.id}`); }}
        onMouseEnter={enter}
        onMouseLeave={leave}
        onFocus={enter}
        onBlur={leave}
        title={open || unread ? undefined : board.name}
      >
        <Emblem name={board.name} icon={board.icon} color={board.color} size="xs" />
        <span className="rail-hide">{board.name}</span>
        {unread > 0 && <span className="count">{unread > 99 ? '99+' : unread}</span>}
      </button>

      <Popover anchor={anchorRef} open={open} onClose={() => setOpen(false)} width={300}>
        <div
          className="board-notif-pop"
          onMouseEnter={() => clearTimeout(timer.current)}
          onMouseLeave={leave}
        >
          <div className="board-notif-head">
            {unread} unread on {board.name}
          </div>
          {preview.map((n) => (
            <button
              key={n.id}
              type="button"
              className="board-notif-row"
              onClick={() => openNotification(n)}
            >
              <span className="board-notif-title">{n.title}</span>
              {n.body && <span className="board-notif-body">{n.body}</span>}
              <span className="board-notif-time">{relativeTime(n.createdAt)}</span>
            </button>
          ))}
          {unread > preview.length && (
            <div className="board-notif-more">
              and {unread - preview.length} more - open the bell for the full list
            </div>
          )}
        </div>
      </Popover>
    </>
  );
}

function NotificationBell() {
  const app = useApp();
  const menu = useDisclosure();
  /* "Unread" survives closing and reopening the bell but not a reload: it is a
     way of reading the list, not a setting. */
  const [onlyUnread, setOnlyUnread] = useState(false);
  const shown = onlyUnread ? app.notifications.filter((n) => !n.read) : app.notifications;

  const openNotification = (notification) => {
    if (!notification.read) app.markNotificationRead(notification.id);
    menu.close();
    // "Added you to a workspace": open that workspace rather than a board.
    if (notification.workspaceId && !notification.boardId) {
      app.loadWorkspaces(notification.workspaceId).then(() => navigate('/'));
      return;
    }
    const target = notificationPath(notification);
    if (target) navigate(target);
    else app.toast('That alert is not tied to a board any more', 'error');
  };

  return (
    <>
      <button
        type="button"
        className={`icon-btn${menu.open ? ' active' : ''}`}
        ref={menu.anchor}
        onClick={menu.toggle}
        aria-label={`Notifications${app.unread ? `, ${app.unread} unread` : ''}`}
      >
        <Icon.Bell size={17} />
        {/* A circle stays a circle: past 9 the exact number stops mattering
            and "9+" keeps the badge round instead of stretching to a pill. */}
        {app.unread > 0 && <span className="badge-dot">{app.unread > 9 ? '9+' : app.unread}</span>}
      </button>

      <Popover anchor={menu.anchor} open={menu.open} onClose={menu.close} align="end" className="notif-panel">
        <div className="notif-head">
          <strong style={{ fontSize: 14 }}>Notifications</strong>
          <div style={{ flex: 1 }} />
          {app.unread > 0 && (
            <button type="button" className="btn ghost sm" onClick={app.markAllRead}>Mark all read</button>
          )}
        </div>
        <div className="notif-filter" role="tablist" aria-label="Filter notifications">
          <button
            type="button"
            role="tab"
            aria-selected={!onlyUnread}
            className={onlyUnread ? '' : 'active'}
            onClick={() => setOnlyUnread(false)}
          >
            All
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={onlyUnread}
            className={onlyUnread ? 'active' : ''}
            onClick={() => setOnlyUnread(true)}
          >
            Unread{app.unread > 0 ? ` (${app.unread})` : ''}
          </button>
        </div>
        <div className="notif-list">
          {!shown.length && (onlyUnread
            ? <EmptyState icon={Icon.Check} title="All caught up" text="Nothing unread. Switch to All for the history." />
            : <EmptyState icon={Icon.Bell} title="Nothing yet" text="Assignments, mentions and deadlines land here." />)}
          {shown.map((notification) => {
            const style = NOTIFICATION_STYLE[notification.kind] || NOTIFICATION_STYLE.update;
            return (
              <SwipeRow
                key={notification.id}
                notification={notification}
                style={style}
                onOpen={() => openNotification(notification)}
                onToggleRead={() => (notification.read
                  ? app.markNotificationUnread(notification.id)
                  : app.markNotificationRead(notification.id))}
                onDelete={() => app.deleteNotification(notification.id)}
              />
            );
          })}
        </div>
      </Popover>
    </>
  );
}

function UserMenu({ onNewWorkspace }) {
  const app = useApp();
  const menu = useDisclosure();
  const isPhone = useIsPhone();
  /* Local while it is being typed, saved on blur or Enter. Seeded from the
     saved value each time the menu opens, so an abandoned edit does not
     survive as a draft that looks like it was set. */
  const [message, setMessage] = useState('');
  useEffect(() => {
    if (menu.open) setMessage(app.user?.availability_message ?? '');
  }, [menu.open, app.user?.availability_message]);

  const saveMessage = async () => {
    const next = message.trim();
    if (next === (app.user?.availability_message ?? '')) return;
    await app.setStatusMessage(next);
  };

  return (
    <>
      <button
        type="button"
        ref={menu.anchor}
        onClick={menu.toggle}
        style={{ border: 0, background: 'none', padding: 0, cursor: 'pointer' }}
        aria-label="Account menu"
      >
        <Avatar person={app.user} size="md" title={app.user?.name} presence={app.presenceOf(app.user?.id)} />
      </button>
      <Popover anchor={menu.anchor} open={menu.open} onClose={menu.close} align="end" width={224}>
        <div style={{ padding: '8px 10px 10px', display: 'flex', gap: 10, alignItems: 'center' }}>
          <Avatar person={app.user} size="md" />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 650, fontSize: 13.5 }}>{app.user?.name}</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {app.user?.email}
            </div>
          </div>
        </div>
        <MenuSeparator />
        {/* Declared status. "Automatic" is the default life: green while
            active, amber while idle, grey when gone - nothing to remember to
            unset. Busy and On leave are the two declarations worth a word,
            and each row shows its dot so the menu teaches the colours. */}
        <MenuLabel>Status</MenuLabel>
        {[
          { value: null, dot: 'available', label: 'Automatic', hint: 'from activity' },
          { value: 'busy', dot: 'busy', label: 'Busy' },
          { value: 'leave', dot: 'leave', label: 'On leave' },
        ].map((option) => (
          <MenuItem
            key={option.label}
            onClick={async () => { await app.setAvailability(option.value); menu.close(); }}
          >
            <span className={`presence-dot ${option.dot}`} style={{ position: 'static', boxShadow: 'none' }} />
            {option.label}
            {option.hint && <span style={{ color: 'var(--muted)', fontSize: 11 }}> {option.hint}</span>}
            {(app.user?.availability ?? null) === option.value && (
              <span style={{ marginLeft: 'auto' }}><Icon.Check size={13} /></span>
            )}
          </MenuItem>
        ))}

        {/* The sentence the dot cannot say. Deliberately not one of the rows
            above: a message is worth leaving while status stays automatic
            ("head down today"), and a status is worth setting with nothing to
            add, so clearing one must not clear the other. Not a MenuItem
            either - those close the menu on click, and this one is typed in. */}
        <div className="status-message">
          <textarea
            className="status-message-input"
            rows={2}
            maxLength={STATUS_MESSAGE_MAX}
            value={message}
            placeholder="Add a status message"
            aria-label="Status message"
            onChange={(event) => setMessage(event.target.value)}
            onBlur={saveMessage}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                event.currentTarget.blur();
              }
              if (event.key === 'Escape') {
                setMessage(app.user?.availability_message ?? '');
                event.currentTarget.blur();
              }
            }}
          />
          <div className="status-message-foot">
            <span>{message.trim() ? `${STATUS_MESSAGE_MAX - message.length} left` : 'Shown when someone writes to you'}</span>
            {(app.user?.availability_message || message.trim()) && (
              <button
                type="button"
                className="status-message-clear"
                onClick={async () => { setMessage(''); await app.setStatusMessage(''); }}
              >
                Clear
              </button>
            )}
          </div>
        </div>

        <MenuSeparator />
        {/* On a phone the sidebar, and the switcher in it, are not there. This
            is the only menu that is, so it carries the workspaces too - the
            same list and the same New item, and nothing on desktop, where
            the switcher is a click away and a second copy would only
            disagree with it. */}
        {isPhone && (
          <>
            <MenuLabel>Workspaces</MenuLabel>
            {app.workspaces.map((workspace) => (
              <MenuItem
                key={workspace.id}
                selected={workspace.id === app.workspaceId}
                onClick={() => { app.switchWorkspace(workspace.id); menu.close(); }}
              >
                <Emblem name={workspace.name} icon={workspace.icon} color="var(--brand)" size="xs" />
                {workspace.name}
              </MenuItem>
            ))}
            <MenuItem icon={Icon.Plus} onClick={() => { menu.close(); onNewWorkspace?.(); }}>
              New workspace
            </MenuItem>
            <MenuSeparator />
          </>
        )}
        <MenuItem icon={Icon.Settings} onClick={() => { menu.close(); navigate('/settings'); }}>
          Settings
        </MenuItem>
        <MenuItem icon={Icon.Users} onClick={() => { menu.close(); navigate('/people'); }}>
          Invite teammates
        </MenuItem>
        <MenuSeparator />
        <MenuItem icon={Icon.LogOut} danger onClick={app.signOut}>Sign out</MenuItem>
      </Popover>
    </>
  );
}

/* ---------------------------------------------------------------- new board */

function NewBoardModal({ open, onClose }) {
  const app = useApp();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState(BOARD_COLORS[0]);
  const [busy, setBusy] = useState(false);
  const nameRef = useRef(null);

  useEffect(() => {
    if (open) { setName(''); setDescription(''); setColor(BOARD_COLORS[0]); }
  }, [open]);

  const submit = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    // create_board returns the board in get_board() shape, so `result` here is
    // the board itself rather than the old { board } envelope.
    const result = await app.guard(async () => unwrap(await supabase.rpc('create_board', {
      p_workspace_id: app.workspaceId,
      p_name: name.trim(),
      p_description: description.trim(),
      p_color: color,
    })));
    setBusy(false);
    if (result) {
      await app.refreshBoards();
      onClose();
      app.toast(`Board “${result.name}” created`, 'success');
      navigate(`/boards/${result.id}`);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New board"
      subtitle="Boards start with Owner, Status, Priority, Due date, Timeline and Notes columns."
      initialFocus={nameRef}
      footer={(
        <>
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="button" className="btn primary" onClick={submit} disabled={!name.trim() || busy}>
            {busy ? <span className="spinner" /> : 'Create board'}
          </button>
        </>
      )}
    >
      <Field label="Board name">
        {(id) => (
          <input
            id={id}
            ref={nameRef}
            className="input"
            value={name}
            placeholder="Q3 Roadmap"
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter') submit(); }}
          />
        )}
      </Field>
      <Field label="Description" hint="Optional. Shown on the dashboard card.">
        {(id) => (
          <input
            id={id}
            className="input"
            value={description}
            placeholder="What this board tracks"
            onChange={(event) => setDescription(event.target.value)}
          />
        )}
      </Field>
      <Field label="Colour">
        <ColorSwatchPicker value={color} onChange={setColor} colors={BOARD_COLORS} />
      </Field>
    </Modal>
  );
}

/**
 * Creating a workspace is one question, what it is called, so it is one box,
 * opened from the switcher it belongs to. It used to be a field at the bottom
 * of Settings, which meant leaving whatever you were doing, scrolling past
 * your profile and the rename form, and finding it there. The owner
 * membership comes from the on_workspace_created trigger, not from here.
 */
function NewWorkspaceModal({ open, onClose }) {
  const app = useApp();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const nameRef = useRef(null);

  useEffect(() => { if (open) setName(''); }, [open]);

  const submit = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    const result = await app.guard(async () => unwrap(await supabase
      .from('workspaces')
      .insert({ name: name.trim(), owner_id: app.user.id })
      .select()
      .single()));
    setBusy(false);
    if (result) {
      await app.loadWorkspaces(result.id);
      app.switchWorkspace(result.id);
      onClose();
      app.toast(`Workspace “${result.name}” created`, 'success');
      // Land on the new workspace's dashboard rather than wherever the switcher
      // was opened from: a board URL from the old workspace means nothing here.
      navigate('/');
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New workspace"
      subtitle="Separate boards, separate members. Invite people once it exists."
      initialFocus={nameRef}
      footer={(
        <>
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="button" className="btn primary" onClick={submit} disabled={!name.trim() || busy}>
            {busy ? <span className="spinner" /> : 'Create workspace'}
          </button>
        </>
      )}
    >
      <Field label="Workspace name">
        {(id) => (
          <input
            id={id}
            ref={nameRef}
            className="input"
            value={name}
            placeholder="Marketing"
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter') submit(); }}
          />
        )}
      </Field>
    </Modal>
  );
}
